import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { MouseEvent, useMemo, useState } from 'react';
import SubjectIcon from '@mui/icons-material/Subject';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { isAutogeneratedRow, type GridRenderCellParams } from '@mui/x-data-grid-premium';
import { Box, Button, ClickAwayListener, Divider, IconButton, Popover, Stack, Typography } from '@mui/material';

type RelatedContactLike = {
  nameSurname?: unknown;
  email?: unknown;
  phoneNumber?: unknown;
};

type RowModel = {
  emails?: unknown;
  relatedContacts?: unknown;
};

type Mode = 'auto' | 'emails' | 'relatedContacts';

const toEmailArray = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw
      .map((x) => (typeof x === 'string' ? x : String(x)))
      .map((x) => x.trim())
      .filter(Boolean);
  }

  if (typeof raw === 'string') {
    return raw
      .split(/[;,]\s*|\s+/g)
      .map((x) => x.trim())
      .filter(Boolean);
  }

  return [];
};

type NormalizedContact = { nameSurname?: string; email: string };
type Normalized = { contacts: NormalizedContact[]; emails: string[] };

const normalizeContacts = (raw: unknown): NormalizedContact[] => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  if (typeof raw[0] !== 'object' || raw[0] === null) return [];

  return (raw as RelatedContactLike[])
    .map((x) => {
      const email = typeof x.email === 'string' ? x.email.trim() : String(x.email ?? '').trim();
      if (!email) return null;

      const nameSurname = typeof x.nameSurname === 'string' ? x.nameSurname.trim() : String(x.nameSurname ?? '').trim();

      return { email, nameSurname: nameSurname || undefined };
    })
    .filter(Boolean) as NormalizedContact[];
};

const normalizeAll = (rawValue: unknown, row: RowModel, enableRowFallback: boolean): Normalized => {
  const raw = enableRowFallback ? (rawValue ?? row.relatedContacts ?? row.emails) : rawValue;

  const contacts = normalizeContacts(raw);
  const emails = contacts.length > 0 ? contacts.map((c) => c.email) : toEmailArray(raw);

  return { contacts, emails };
};

type SummaryPopoverContentProps = {
  lines: { key: string; text: string }[];
  onCopyAll: () => void;
};

const SummaryPopoverContent = ({ lines, onCopyAll }: SummaryPopoverContentProps) => {
  const intl = useIntl();

  return (
    <Box sx={{ width: 360, p: 1 }}>
      <Stack spacing={0.5} sx={{ maxHeight: 240, overflowY: 'auto', pr: 0.5 }}>
        {lines.map((l) => (
          <Typography key={l.key} variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {l.text}
          </Typography>
        ))}
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button size="small" startIcon={<ContentCopyIcon />} onClick={onCopyAll}>
          {intl.formatMessage({ id: 'common.copyAll' })}
        </Button>
      </Box>
    </Box>
  );
};

type SummaryCellViewProps = {
  params: GridRenderCellParams;
  mode?: Mode;
  enableRowFallback?: boolean;
};

const SummaryCellView = ({ params, mode = 'auto', enableRowFallback = true }: SummaryCellViewProps) => {
  // other hooks
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // dynamic variables
  const rowEmailsRaw = (params.row as RowModel | undefined)?.emails;
  const rowRelatedContactsRaw = (params.row as RowModel | undefined)?.relatedContacts;

  const normalized = useMemo(
    () =>
      normalizeAll(params.value, { emails: rowEmailsRaw, relatedContacts: rowRelatedContactsRaw }, enableRowFallback),
    [params.value, enableRowFallback, rowEmailsRaw, rowRelatedContactsRaw]
  );

  const effectiveMode: Mode = useMemo(() => {
    if (mode !== 'auto') return mode;
    return normalized.contacts.length > 0 ? 'relatedContacts' : 'emails';
  }, [mode, normalized.contacts.length]);

  const count = useMemo(() => {
    return effectiveMode === 'relatedContacts' ? normalized.contacts.length : normalized.emails.length;
  }, [effectiveMode, normalized.contacts.length, normalized.emails.length]);

  const lines = useMemo(() => {
    if (effectiveMode === 'relatedContacts') {
      return normalized.contacts.map((c) => {
        const text = c.nameSurname ? `${c.nameSurname} - ${c.email}` : c.email;

        return { key: `${c.email}-${c.nameSurname ?? ''}`, text };
      });
    }

    return normalized.emails.map((email) => ({ key: email, text: email }));
  }, [effectiveMode, normalized.contacts, normalized.emails]);

  const countLabel = useMemo(() => {
    return effectiveMode === 'relatedContacts'
      ? intl.formatMessage({ id: 'effortContacts.related.count' }, { count })
      : intl.formatMessage({ id: 'effortContacts.emails.count' }, { count });
  }, [effectiveMode, intl, count]);

  const open = Boolean(anchorEl);

  if (isAutogeneratedRow(params.row)) {
    return null;
  }

  if (count === 0) return <span>-</span>;

  // sync helper functions
  const handleOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e?: unknown) => {
    if (e && typeof (e as any).stopPropagation === 'function') {
      (e as any).stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleCopyAll = async () => {
    await navigator.clipboard.writeText(lines.map((l) => l.text).join('; '));

    enqueueSnackbar(intl.formatMessage({ id: 'effortContacts.emails.copied' }), { variant: 'success' });
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', userSelect: 'none' }}>
        <span>{countLabel}</span>

        <IconButton size="small" onClick={handleOpen} aria-label={countLabel}>
          <SubjectIcon fontSize="inherit" />
        </IconButton>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { borderRadius: 2 },
          },
        }}>
        <ClickAwayListener onClickAway={handleClose}>
          <Box>
            <SummaryPopoverContent lines={lines} onCopyAll={handleCopyAll} />
          </Box>
        </ClickAwayListener>
      </Popover>
    </>
  );
};

const SummaryCell = (opts?: { mode?: Mode; enableRowFallback?: boolean }) => (params: GridRenderCellParams) => (
  <SummaryCellView params={params} mode={opts?.mode ?? 'auto'} enableRowFallback={opts?.enableRowFallback ?? true} />
);

export default SummaryCell;
